import Box from "@mui/material/Box";
import { useCallback, useEffect, useRef, useState } from "react";
import { useGetDeliveryNotesService } from "subModule/src/services/invoice/useInvoices";
import { useSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { useTranslation } from "react-i18next";
import { Button, CircularProgress, SelectChangeEvent } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import Loader from "layout/Loader";
import { useGetStationDropdownService } from "subModule/src/services/station/useStation";
import TableWithPagination from "common/Table/Table";
import DeliveryNoteSearchHeader from "./DeliveryNotesSearchHeader";
import { Controller, useForm } from "react-hook-form";
import { themes } from 'theme/themes';
import { useSelector } from 'react-redux';
import { brandingInfo } from 'features/server/brandingSlice';
import { Add } from "@mui/icons-material";
import FormDialog from "common/Modal/Modal";
import DynamicSelect from "common/Select/Select";
import { InvoiceProfileService } from "subModule/src/core/services";
import { GetInvoiceByIdResponse } from "subModule/src/services/invoice/interface";
import { checkPrivileges } from "Helpers/CheckPrivileges";
interface SearchFormData {
  [key: string]: string | any;
}
interface DeliveryNoteProps {
  userId?: number;
  accountId?: number;
  type?: 'client';
}
export default function DeliveryNotesListing(props: DeliveryNoteProps) {
  const navigate = useNavigate();
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const { t } = useTranslation();
  const branding = useSelector(brandingInfo);
  const themeColors = branding?.attributes?.themeId === 100 ? branding?.attributes?.customTheme : (themes as any)[branding?.attributes?.themeId || 3];
  const { control, watch, setValue, formState: { errors }, handleSubmit, reset } = useForm({});
  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(props?.type ? 5 : 20);
  const [loading, setLoading] = useState<boolean>(false);
  const [filter, setFilter] = useState<string[]>(["vehiclePlate", "invoiceStatus"]);
  const [search, setSearch] = useState<any>({ invoiceStatus: '' });
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>('');
  const [dropdownPage, setDropdownPage] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [creating, setCreating] = useState<boolean>(false);
  const [searchFormData, setSearchFormData] = useState<SearchFormData>({
    deliveryNoteNo: "",
    stationId: 0,
    stationName: '',
    vehiclePlate: "",
    vehicleVin: "",
    invoiceStatus: '',
    clientName: '',
    start: null,
    end: null
  });
  const {
    data: tableData,
    isFetching,
    refetch,
  } = useGetDeliveryNotesService({ 
    pageNumber: pageNo, size: pageSize, ...search, 
    ...(props?.accountId && { accountId: props.accountId }), 
    ...(props?.userId && { userId: props.userId }) 
  });
  const { data: stationData, refetch: stationRefetch } =
    useGetStationDropdownService({ pageNumber: dropdownPage, size: 50, name: dropdownSearchTerm });
  const DeliveryNoteStatusList = [
    { label: t('all'), value: '' },
    { label: t('pendingInvoice'), value: 2 },
    { label: t('invoiced'), value: 1 },
  ];
  const { enqueueSnackbar } = useSnackbar();
  const showRowView = (row: any) => navigate("/delivery-notes/" + row.id);
  const fetchData = useCallback(() => {
    refetch().then((res: any) => {
      if (!res?.data?.pages[0]?.success) enqueueSnackbar(<AlertMessages text={res?.data?.pages[0]?.message || "Something went wrong"} />, { variant: "error" });
    }).finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    fetchData();
  }, [pageNo, pageSize, search]);
  useEffect(() => {
    stationRefetch();
  }, []);
  useEffect(() => {
    if (loading) handleSubmitSearch();
  }, [searchFormData.invoiceStatus]);
  useEffect(() => {
    if (loading) handleSubmitSearch();
  }, [searchFormData.start, searchFormData.end]);
  const handleDropdownActions = (typeName: string, action: 'searchIt' | 'loadNext', search?: string) => {
    if(action === 'searchIt') {
      setDropdownPage(1);
      setDropdownSearchTerm(search);
      if(search) setSearchLoading(typeName);
    } else {
      setDropdownPage(dropdownPage + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if(typeName === 'stations') stationRefetch().finally(() => setSearchLoading(null));
    }, 300);
  }
  const handleChangePage = (pageNo: number) => {
    setPageNo(pageNo + 1);
  };
  const handleChangeRows = (pageSize: number) => {
    setPageSize(pageSize);
    setPageNo(1);
  };
  const clearSearch = () => {
    setFilter(["vehiclePlate", "invoiceStatus"]);
    setSearchFormData({
      deliveryNoteNo: "",
      stationId: 0,
      vehiclePlate: "",
      vehicleVin: "",
      invoiceStatus: '',
      clientName: '',
      start: null,
      end: null
    });
    setSearch({ invoiceStatus: '' });
  };
  const handleChangeFilter = (event: SelectChangeEvent<typeof filter>) => {
    const { target: { value }, } = event;
    const selectedFilters = typeof value === 'string' ? value.split(',') : value;
    setFilter(selectedFilters);
    setSearchFormData((prevFormData) => {
      const updatedFormData = Object.fromEntries(
        Object.keys(prevFormData).map((key) => [
          key,
          value.includes(key)
            ? prevFormData[key]
            : typeof prevFormData[key] === "string"
              ? ""
              : 0,
        ])
      );
      if (selectedFilters.includes('stationId') && prevFormData.stationName)
      updatedFormData.stationName = prevFormData.stationName;
      if (selectedFilters.includes('dateInterval')) {
        updatedFormData.start = prevFormData.start;
        updatedFormData.end = prevFormData.end;
      }
      return updatedFormData;
    });
    if (!value.length && Object.keys(search).length) {
      clearSearch();
    }
  };
  const handleChangeSearch = (field: string, value: any) => {
    const val = (field === "stationId") ? (value ? parseInt(value.value) || 0 : 0)
      : (field === 'invoiceStatus') ? (value ? value.value : '')
        : value;
    if (field === 'invoiceStatus' && searchFormData.invoiceStatus === val) return;
    if (field === 'dateInterval') {
      setSearchFormData((prevData) => ({
        ...prevData,
        start: value[0],
        end: value[1]
      }));
      if ((value[0] && value[1]) || ((value[0] === null) && (value[1] === null))) {
        setLoading(true);
      }
    } else {
      setSearchFormData((prevData) => ({
        ...prevData,
        [field]: val,
        ...(field === 'stationId' && value?.label ? { stationName: value.label } : {}),
      }));
    }
    if (field === "invoiceStatus") setLoading(true);
  };
  const handleSubmitSearch = () => {
    const filteredSearch: Record<string, string | number | boolean> = {};
    const { start, end, ...rest } = searchFormData;
    if (start && end) {
      filteredSearch.start = start;
      filteredSearch.end = end;
    } else {
      // ❗ If one or both missing → remove both
      setSearchFormData((prev) => ({
        ...prev,
        start: null,
        end: null,
      }));
    }
    Object.entries(rest).forEach(([key, value]) => {
      if (value) {
        filteredSearch[key] = typeof value === "string" ? value?.trim() : value;
      }
    });
    setSearch(filteredSearch);
    setPageNo(1);
    setPageSize(pageSize);
  };
  const onSubmit = (data: any) => {
    setCreating(true);
    InvoiceProfileService.createConsolidatedInvoices({
      deliveryNoteIds: data.deliveryNoteIds,
      clientId: props?.userId || 0,
      stationId: data?.stationId?.value || null,
      accountId: props?.accountId || 0,
    }).then((response: GetInvoiceByIdResponse) => {
      if(response.success) {
        enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('consolidatedInvoice') + ' ' + t('created') + ' ' + t('successfully')} />, { variant: 'success' });
        setOpenModal(false);
        refetch();
        reset();
      } else {
        enqueueSnackbar(<AlertMessages text={response?.message} />, { variant: 'error' });
      }
    }).catch((err:any) => { 
      console.error("===", err);
      enqueueSnackbar(<AlertMessages text={err?.message || err?.data?.message || "Something went wrong"} />, { variant: 'error' });
    }).finally(() => setCreating(false));
  }
  return (
    <Box>
      {props?.type ? null : <ComponentHeaderTwo
        showBtn={true}
        showSaveBtn={false}
        onClick={() => navigate("/delivery-notes")}
        heading={t("deliveryNotes")}
        icon={<NoteAltOutlinedIcon sx={{ fill: '#000', width: '30px', height: '30px', marginTop: '3px' }} />}
        btnText={"create"}
        btnType={"create"}
        isBtnEnabled={false}
        showCreateBtn={false}
      />}
      {(isFetching || loading) ? (
        <Loader />
      ) : (
        <TableWithPagination
          columns={[
            { id: 'vehicleDetail', label: 'vehicle', numeric: false },
            { id: 'plate', label: 'plateNumber', numeric: false },
            { id: 'deliveryNoteNo', label: 'deliveryNoteNo', numeric: false },
            { id: 'customerName', label: 'customerName', numeric: false },
            { id: 'invoiceStatusDeliveryNote', label: 'status', numeric: false },
            { id: 'stationName', label: 'station', numeric: false },
            { id: 'totalAmount', label: 'amount', numeric: false },
            { id: 'balanceAmount', label: 'dueAmount', numeric: false },
            { id: 'createdAt', label: 'createdAt', numeric: false },
          ]}
          data={tableData?.pages[0]?.data?.content || []}
          tableData={tableData?.pages[0]?.data || {}}
          handleChangePage={handleChangePage}
          handleChangeRows={handleChangeRows}
          rowClick={checkPrivileges("ROLE_DELIVERY_NOTE_VIEW")}
          showRowView={showRowView}
          type="invoice"
          {...{ pageNo, pageSize }}
          loadingSearch={false}
          showCheckbox={true}
          form={{ control, watch, setValue }}
          headerComponent={<>
            <DeliveryNoteSearchHeader
              searchFormData={searchFormData}
              handleChangeFilter={handleChangeFilter}
              handleChangeSearch={handleChangeSearch}
              handleSubmitSearch={handleSubmitSearch}
              clearSearch={clearSearch}
              stationData={stationData?.pages[0]?.data?.content || []}
              stationResponse={stationData || {}}
              handleDropdownActions={handleDropdownActions}
              {...{ filter, search, DeliveryNoteStatusList, dropdownPage, searchLoading, dropdownSearchTerm, setDropdownSearchTerm, setDropdownPage }}
            />
            {watch('deliveryNoteIds')?.length > 0 ? <Button id="import-button" variant="contained" disableElevation
              startIcon={<Add />}
              className={`bg-white font-Saira font-[600] text-[12px] h-[31px] px-5 py-1 cursor-pointer rounded-[6px] ms-[10px] capitalize`}
              sx={{
                outline: "2px solid",
                color: themeColors?.secondary,
                borderColor: themeColors?.secondary,
              }}
              onClick={() => setOpenModal(true)}
            ><span className="whitespace-nowrap">{t("consolidatedInvoice")}</span></Button> : null}
          </>}
          showHeader={true}
          showSearch={true}
          showFilter={true}
        />
      )}
      {openModal ? 
        <FormDialog
          open={openModal}
          onClose={(event: any, reason: any) => {
            if(event && (reason !== 'backdropClick')) setOpenModal(false);
          }}
          title={t('create') + ' ' + t('consolidatedInvoice')}
          createPopup={true}
          hideActions={true} 
          content={<Box component='form' className="flex items-end gap-2" id='form' onSubmit={handleSubmit(onSubmit)}>
            <Controller
              name="stationId"
              control={control}
              defaultValue={null}
              rules={{ required: t('This field is required.') }}
              render={({ field }) => (<Box className='w-[250px]'>
                <DynamicSelect
                  label={t('station') + ' *'}
                  {...field}
                  options={(stationData?.pages[0]?.data?.content || [])?.map((item: { id: number, name: string }) => ({
                    value: item.id, 
                    label: item.name
                  }))}
                  isSearchable
                  onChange={(selectedOption: any) => { field.onChange(selectedOption); }}
                  error={errors.stationId?.message as string}
                  isLoading={searchLoading === 'stations'}
                  onInputChange={(search: string, { action }: { action: string }) => {
                    if(action === 'input-change' || 
                      (action !== 'set-value' && action !== 'menu-close' && action !== 'input-blur')
                    ) handleDropdownActions("stations", 'searchIt', search);
                    else if(action === 'input-blur' || action === 'menu-close') {
                      if(dropdownSearchTerm || pageNo > 1) handleDropdownActions("stations", 'searchIt', '');
                      else setDropdownSearchTerm('');
                      setPageNo(1);
                    }
                  }}
                  onMenuScrollToBottom={() => {if(!(stationData?.pages[0]?.data as any).last) handleDropdownActions("stations", 'loadNext')}}
                  page={pageNo}
                />
              </Box>)}
            />
            <Button type="submit" disabled={creating} className="primary h-[38px] mb-[10px]" style={{ background: themeColors?.primary }} variant="contained" disableElevation>
              {creating && <CircularProgress
                size={15}
                sx={{ color: '#fff', position: 'absolute', left: '7px' }}
              />}
              {t('submit')}
            </Button>
          </Box>}
        />
      : null}
    </Box>
  );
}